import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../../../../components/tooltip/Tooltip';
import { PathsLayouts } from '../../../../../config/routes';
import RiskLevelBadge from '../../RiskLevelBadge';
import { BadgeStatus } from '../BadgeStatus';
import { StudentRiskItem } from '../types';
import { useTexts } from './useTexts';
import { TranslationsKeys } from '../../../../../locales/translations';

export function useColumns() {
  const texts = useTexts().table;
  const headerTexts = texts.headers;
  const contentTexts = texts.content;

  const { t } = useTranslation(TranslationsKeys.STUDENT_RISK_HISTORY);
  const history = useHistory();

  const handleShowDetails = useCallback(
    (sessionEnrollmentId: number | string) => {
      history.push(
        `${PathsLayouts.studentWarnings}/student-risk/history/session-enrollment/${sessionEnrollmentId}`,
      );
    },
    [history],
  );
  const columns: ColumnTable<StudentRiskItem>[] = useMemo(() => {
    return [
      {
        headerText: headerTexts.date,
        columnName: 'date',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: headerTexts.session,
        columnName: 'session',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: headerTexts.studentInfo,
        columnName: 'studentInfo',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return (
            <div>
              {value.name} {value.id}
            </div>
          );
        },
      },
      {
        headerText: headerTexts.studyPlan,
        columnName: 'studyPlan',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: headerTexts.campus,
        columnName: 'campus',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: headerTexts.promotion,
        columnName: 'promotion',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: headerTexts.course,
        columnName: 'course',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: headerTexts.riskLevel,
        columnName: 'riskLevel',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value, index }) =>
          value ? (
            <div className="d-flex justify-content-center align-items-center gap-2">
              <div style={{ width: '55px', textAlign: 'left' }}>
                <RiskLevelBadge riskLevel={value.calculated} />
              </div>
              <div style={{ width: '24px', paddingBottom: 2 }}>
                {value.defined && (
                  <Tooltip
                    id={`tooltip-${index}`}
                    text={`Recategorizado: Riesgo ${t(
                      `table.content.riskLevels.${value.defined}`,
                    )} ${value.date}`}
                  >
                    <Icon name="information" />
                  </Tooltip>
                )}
              </div>
            </div>
          ) : (
            '-'
          ),
      },
      {
        headerText: headerTexts.isActive,
        columnName: 'isActive',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return <BadgeStatus active={row.state} />;
        },
      },
      {
        headerText: headerTexts.conditional,
        columnName: 'conditional',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value
            ? contentTexts.conditionality.yes
            : contentTexts.conditionality.no;
        },
      },
      {
        headerText: headerTexts.action,
        columnName: 'action',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return (
            <Button
              size="sm"
              text={contentTexts.detailButton}
              onClick={() => handleShowDetails(row.sessionEnrollmentId)}
            />
          );
        },
      },
    ];
  }, [headerTexts, contentTexts, handleShowDetails, t]);

  return columns;
}
